import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keeps a scroll container pinned to the bottom while content grows, but only when the user is already
 * within `threshold` px of the bottom. Otherwise `showJump` turns on so the UI can offer "Latest".
 *
 * @param {*} dep  changes whenever content may have grown (e.g. the messages array)
 */
export default function useAutoScroll(dep, { threshold = 80 } = {}) {
  const ref = useRef(null);
  const pinned = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const isNearBottom = (el) => el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const near = isNearBottom(el);
    pinned.current = near;
    setShowJump((prev) => (prev === !near ? prev : !near));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const el = ref.current;
    if (!el) return;
    pinned.current = true;
    setShowJump(false);
    if (typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight, behavior });
    else el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (pinned.current) {
      // instant while streaming: smooth scrolling every frame lags behind the text
      el.scrollTop = el.scrollHeight;
    } else if (!isNearBottom(el)) {
      setShowJump(true);
    }
  }, [dep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Content can also grow without a state change (images loading, popovers): follow it when pinned.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const inner = el.firstElementChild;
    if (!inner) return undefined;
    const ro = new ResizeObserver(() => {
      if (pinned.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, [dep]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ref, onScroll, showJump, scrollToBottom, pin: () => { pinned.current = true; } };
}
